/**
 * CommunityCard Component
 * Card for displaying a community in grids
 * Used in the Communities tab and other community listings
 */

import { LockIcon } from "@components/icons/index"
import { useNavigate } from "react-router-dom"
import type { CommunityCard as CommunityCardType } from "@/features/communities/types/index"
import { cn } from "@/lib"

interface CommunityCardProps {
  community: CommunityCardType
  className?: string
}

export function CommunityCard({ community, className }: CommunityCardProps) {
  const navigate = useNavigate()

  const handleClick = () => {
    navigate(`/communities/${community.id}`)
  }

  return (
    <div
      onClick={handleClick}
      className={cn(
        "relative bg-card rounded-xl overflow-hidden flex flex-col shadow-sm cursor-pointer hover:opacity-90 transition-opacity",
        className,
      )}
    >
      {/* Image Placeholder */}
      <div className="aspect-[16/11] bg-brand-beige overflow-hidden">
        {community.coverImage ? (
          <img
            src={community.coverImage}
            alt={community.name}
            className="w-full h-full object-fill"
          />
        ) : (
          <div className="w-full h-full" />
        )}
      </div>

      {/* Content */}
      <div className="absolute bottom-0 px-4 py-6 w-full max-h-[90px] md:max-h-[130px] bg-gradient-to-t from-[rgba(0,0,0,0.7)] to-[rgba(0,0,0,0)]">
        <h3 className="section-heading text-sm md:text-[18px] leading-[22px] tracking-normal text-white mb-1">
          {community.name}
          {community.type === "PRIVATE" && <LockIcon className="inline ml-2 w-5 h-5 mb-1" />}
        </h3>
        <p className="text-white text-[12px] md:text-[14px] my-1">
          {community.memberCount} members &nbsp; &nbsp; {community.ritualCount} Rituals
        </p>
        {community.city && community.state && (
          <p className="font-normal text-[12px] md:text-[14px] leading-[100%] tracking-normal text-white md:my-1">
            {community.city}, {community.state}
          </p>
        )}
      </div>
    </div>
  )
}
